import { SnackbarService } from 'app/shared/service/snackbar.service';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FuseConfigService } from '@fuse/services/config';
import { AppConfig, Themes } from 'app/core/config/app.config';
import { Layout } from 'app/layout/layout.types';
import { ColorPickerControl, ColorPickerModule } from '@iplab/ngx-color-picker';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { fuseAnimations } from '@fuse/animations';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateConfigService } from 'app/services/translate-config.service';
import { LANGUAGES_TO_SHOW, LocalStorageKeys } from 'app/shared/misc/shared.constant';
import { OverallFacadeService } from 'app/services/overall-facade.service';
import { SettingsfileService } from './settings.service';
import { MatIconModule } from '@angular/material/icon';
import { FuseDrawerComponent } from '../../../../@fuse/components/drawer';
import { MatButtonModule } from '@angular/material/button';

import { SharedService } from 'app/shared/shared.service';
import { environment } from 'environments/environment';

@Component({
    selector: 'settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None,
    imports: [MatIconModule,
        MatButtonModule,
        ColorPickerModule
    ]
})
export class SettingsComponent implements OnInit, OnDestroy {

    alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: ''
    };

    config: AppConfig;
    layout: Layout;
    scheme: 'dark' | 'light';
    showAlert: boolean = false;
    theme: string;
    themes: Themes;
    togglePicker = false;
    languagesToShow = LANGUAGES_TO_SHOW;
    // languages = SUPPORTED_LANGUAGES;
    // languages = this.overallFacadeService.getSpecificState(OverallStoreEnum.allData)?.['data']?.supportedLanguages;


    private _unsubscribeAll: Subject<any> = new Subject<any>();

    public sketchControl = new ColorPickerControl().setValueFrom('#4f46e5').hideAlphaChannel();

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _fuseConfigService: FuseConfigService,
        private snackBar: MatSnackBar,
        private translateConfigService: TranslateConfigService,
        private overallFacadeService: OverallFacadeService,
        private settingsFileService: SettingsfileService,
        private sharedService: SharedService,
        private snackbarService: SnackbarService
    ) {

    }

    ColorToHex(color: any) {
        const hexadecimal = color.toString(16);
        return hexadecimal.length == 1 ? "0" + hexadecimal : hexadecimal;
    }

    ConvertRGBtoHex(red: any, green: any, blue: any) {
        return "#" + this.ColorToHex(red) + this.ColorToHex(green) + this.ColorToHex(blue);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: AppConfig) => {

                // Store the config
                this.config = config;
            });
        this.domainInfoApi();
    }


    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }


    domainInfoApi(): void {
        this.overallFacadeService.domainInfoApi().subscribe(
            res => this.getUserSuccess(res),
            error => this.userErr(error)
        );
    }

    getUserSuccess({data}: any): void {
        this.redirectMobilePortal(data);
        localStorage.setItem('IsPartner', data['customerData']?.Partner);
        localStorage.setItem('ThemeCode', data['customizations']?.themeCode);
        const enableSignUp = data['customizationsText']?.EnableSignUp ?? false;
        localStorage.setItem('EnableSignUp', JSON.stringify(enableSignUp));
        // localStorage.setItem('EnableSignUp', data['customizationsText']?.EnableSignUp);
        localStorage.setItem('DomainName', data['customerData']?.DomainName);
        localStorage.setItem('URL', data?.['jobLists'][0]?.['URL']);
        localStorage.setItem('Tier', data?.['customerData'].Tier);
        localStorage.setItem('customerId', data?.['jobLists'][0]?.CustomerID);
        localStorage.setItem('authProviders', JSON.stringify(data?.['authProviders']));
        localStorage.setItem('GlobalDecimalSetting', JSON.stringify(data?.['customizationsText']?.GlobalDecimalSetting));
        localStorage.setItem('decimalSeparator', data?.['customizationsText']?.DecimalSeparator || '.');
        localStorage.setItem('customerType', data?.['customerData']?.CustomerType ?? '');
        localStorage.setItem(LocalStorageKeys.AUTO_DISMISSED_ENABLED, data['customizationsText']?.IsAutoDismissEnabled);
        localStorage.setItem(
            LocalStorageKeys.AUTO_DISMISSED_DURATION,
            String(data['customizationsText']?.AutoDismissDuration * 1000)
        );

        /** TODO: Save the Customer autoDismiss Property in local storage and use it in
         *  alert component to auto dismiss the alert based on that value.
         *  Currently setting it to false as default. */
        // localStorage.setItem('customerName', data?.['customerData']?.CustomerName ?? '');

        if (data?.['customizationsText']?.MainSection?.TopSection?.CustomerLogo) {
            localStorage.setItem('loginCustomerLogo', data?.['customizationsText']?.MainSection?.TopSection?.CustomerLogo);
        }
        localStorage.setItem('displayUpload', data?.customizationsText?.DisplayUpload);
        if (data?.['jobLists'][0]?.AutomaticPrintDelivery) {
            localStorage.setItem('automaticDelivery', data?.['jobLists'][0]?.AutomaticPrintDelivery);
        } else {
            localStorage.setItem('automaticDelivery', 'false');
        }
        let theme = 'theme-brand';
        this._fuseConfigService.config = {theme};
        let layout = data?.['customizationsText']?.Layout ? data?.['customizationsText'].Layout : 'dense';
        let scheme = data?.['customizationsText']?.BrandScheme ? data?.['customizationsText'].BrandScheme : 'light';
        this._fuseConfigService.config = {layout};
        this._fuseConfigService.config = {scheme};

        document.documentElement.style.setProperty('--primary-col', data["customizations"].themeCode ? data["customizations"].themeCode : '#4f46e5');
        document.documentElement.style.setProperty('--primary-col-rgb', data["customizations"].Rgb500 ? data["customizations"].Rgb500 : '(79, 70, 229, 1)');
        document.documentElement.style.setProperty('--tbs-primary-50', data["customizations"].Hex50);
        document.documentElement.style.setProperty('--tbs-primary-50-rgb', data["customizations"].Rgb50);
        document.documentElement.style.setProperty('--tbs-primary-100', data["customizations"].Hex100);
        document.documentElement.style.setProperty('--tbs-primary-100-rgb', data["customizations"].Rgb100);
        document.documentElement.style.setProperty('--tbs-primary-200', data["customizations"].Hex200);
        document.documentElement.style.setProperty('--tbs-primary-200-rgb', data["customizations"].Rgb200);
        document.documentElement.style.setProperty('--tbs-primary-300', data["customizations"].Hex300);
        document.documentElement.style.setProperty('--tbs-primary-300-rgb', data["customizations"].Rgb300);
        document.documentElement.style.setProperty('--tbs-primary-400', data["customizations"].Hex400);
        document.documentElement.style.setProperty('--tbs-primary-400-rgb', data["customizations"].Rgb400);
        document.documentElement.style.setProperty('--tbs-primary-500', data["customizations"].Hex500);
        document.documentElement.style.setProperty('--tbs-primary-500-rgb', data["customizations"].Rgb500);
        document.documentElement.style.setProperty('--tbs-primary-600', data["customizations"].Hex600);
        document.documentElement.style.setProperty('--tbs-primary-600-rgb', data["customizations"].Rgb600);
        document.documentElement.style.setProperty('--tbs-primary-700', data["customizations"].Hex700);
        document.documentElement.style.setProperty('--tbs-primary-700-rgb', data["customizations"].Rgb700);
        document.documentElement.style.setProperty('--tbs-primary-800', data["customizations"].Hex800);
        document.documentElement.style.setProperty('--tbs-primary-800-rgb', data["customizations"].Rgb800);
        document.documentElement.style.setProperty('--tbs-primary-900', data["customizations"].Hex900);
        document.documentElement.style.setProperty('--tbs-primary-900-rgb', data["customizations"].Rgb900);
        if (data?.['customizationsText']?.CustomerLanguage) {
            localStorage.setItem('langLocale', data?.['customizationsText']?.CustomerLanguage);
        }
        if (localStorage.getItem('langLocale')) {
            let browserLang = navigator.language;
            let languageSet = false;
            this.languagesToShow.filter(res => {
                if (res === browserLang) {
                    languageSet = true;
                    this.translateConfigService.changeLanguage(res);

                    data?.['supportedLanguages']?.filter(res => {
                        if (res?.locale === browserLang) {
                            localStorage.setItem('dateByCountry', res.date);
                            this.sharedService.updateData(res?.date);
                        }
                    });

                }
            })

            if (!languageSet) {
                const matchedLanguages = this.languagesToShow.filter(res => {
                    if(res === localStorage.getItem('langLocale')) {
                        this.translateConfigService.changeLanguage(localStorage.getItem('langLocale'));
                        return true;
                    }
                    return false;
                })

                if (matchedLanguages.length === 0) {
                    this.translateConfigService.changeLanguage('en');
                }
            }

                data?.['supportedLanguages']?.filter(res => {
                    if (res?.locale === localStorage.getItem('langLocale')) {
                        localStorage.setItem('dateByCountry', res.date);
                        this.sharedService.updateData(res?.date);
                    }
                });
        }
        else {
            this.translateConfigService.changeLanguage('en');
        }
        if (data?.['customizationsText']?.Currency) {
            localStorage.setItem('currency', data?.['customizationsText']?.Currency);
        }
        this.settingsFileService.updatedLocalhostValues.emit(true);
    }

    private redirectMobilePortal(data: any): void {
        const currentPath = this._router.url.split('?')[0];
        const mobilePortalRoutes = ['/user/sign-in', '/public/upload'];
        if (!mobilePortalRoutes.includes(currentPath) || !/Mobi|Android/i.test(navigator.userAgent)) {
            return;
        }

        const activeLocation = data?.locations?.find(location => location.IsActive);
        const domainName = data?.customerData?.DomainName;

        if (!activeLocation?._id || !domainName) {
            return;
        }

        const queryParams = new URLSearchParams({
            locationid: activeLocation._id,
            domainname: domainName
        });

        window.open(`${environment.mobileRedirectURL}?${queryParams.toString()}`, '_self');
    }

    userErr(err: any): void {
        if ((err.error?.error?.message)?.toLowerCase().trim() === 'customer not found') {
            this.snackbarService.showRedErrorSnackbar({
                message: '404 Customer Not Found',
                duration: 3000,
            });
            setTimeout(() => {
                window.open('https://eprintit.com', '_self');
            }, 3000);
        }
    }
}
